firebird.factory('videoSimulcastService', [
    '$window',
    '$timeout',
    'logService',
    'helperService',
    'videoFilterService',
    'videoGlobalService',
    'configService',
    'attendeeListService',
    'executionEnvironmentEnum',
    function (
        $window,
        $timeout,
        logService,
        helperService,
        videoFilterService,
        videoGlobalService,
        configService,
        attendeeListService,
        executionEnvironmentEnum
    ) {
        var service = {
            initialize: initialize
        };

        var self;

        const logger = new LogServiceWrapper(logService, '[videoroomCtrl] videoSimulcastService');

        return service;

        function initialize(selfParam) {
            self = selfParam;

            if (videoGlobalService.test) {
                $window = videoGlobalService.data.$window;
            }

            // when to enable simulcast, based on number of participants.
            // default is to enable for 3+.  To enable always, make it 0; and to never enable, make it like self.simulcastAtMax.
            // use the simulcastEnabled URL param or user pair to control this.
            self.simulcastAt = 3;
            self.simulcastAtMax = 999;

            // whether to enable or disable simulcast2 (transceiver based), or use default if null.
            // default was to use simulcast2 for chrome version 77+
            // default was later updated to false in videoParamsService.
            // possible values: null, true, false, "legacy" or other string.
            self.useSimulcast2 = null;

            self.initSimulcast = initSimulcast;
            self.initSimulcast2 = initSimulcast2;
            self.setSimulcast = setSimulcast;
            self.setSimulcastForAllVideos = setSimulcastForAllVideos;
            self.selectSimulcastSubstream = selectSimulcastSubstream;
            self.verifySimulcastLater1 = verifySimulcastLater1;
            self.verifySimulcastLater2 = verifySimulcastLater2;
            self.verifySimulcastTimeout = 3000;
            self.limitSimulcastTemporal = limitSimulcastTemporal;
            self.cleanupSimulcastTimer = cleanupSimulcastTimer;

            // reason for calling setSimulcast, used for logging.
            // a separate variable instead of passing new param in setSimulcast works well with nested calls,
            // repeated calls, and when reason is known few steps before calling setSimulcast.
            self.simulcastReason = '';

            // publish side when simulcast2 is used.
            self.constraintsForSimulcast2 = { width: 1280, height: 720 }; // lower values do not work.
            self.configForSimulcast2 = {
                high: 500000,
                medium: 400000,
                low: 100000,
                scaleMedium: 2,
                scaleLow: 4,
                // control framerate of other substreams for H264 only.
                fpsAuto: 0,
                fpsMedium: 15,
                fpsLow: 15
            }; // updated via useSimulcast2.

            self.constraintsWithoutSimulcast2 = { width: 640, height: 360 }; // used only for videoFilter, faceTrack or videoCanvas.

            // flag to apply fixes for ANYAM-103384 enabled by default. needed for performance comparison in tests.
            self.fixVideoFilterForSimulcast2 = true;

            // determine what to do when publish bitrate is reduced with simulcast2
            // substream: have the subscriber selected substream 1 instead of 2, if bitrate of 2 is lower than 1.
            // videoLow: have the publisher go to videoLow (bitrate) mode for one minute.
            // encodings: have the publisher adjust the bitrates of substreams proportionately.

            self.bitrateLowDefault = {
                type: 'encodings', // 'substream' or 'videoLow' or 'encodings' or 'none'/null
                cutoffFactor: 0.9, // cutoff for substream 2 bitrate compared to substream 1 bitrate. 0.9 means 90%.
                cutoffScale: 1.2, // cutoff for substream 2 bitrate below which it starts scale for type encodings.
                resetAfter: 60000, // reset after this milliseconds for videoLow or substream type.
                noChangeAfter: 10000 // do not allow change for 10s after previous reset.
            };

            self.bitrateLowConfig = Object.assign({}, self.bitrateLowDefault);

            // can be set when videoLowConfig is enabled, to specify substream bitrates limit.
            self.bitrateForSimulcast2 = null;

            self.updateConstraintsForSimulcast2 = updateConstraintsForSimulcast2;
            self.applyConstraintsForSimulcast2 = applyConstraintsForSimulcast2;
            self.verifyConstraintsForSimulcast2 = verifyConstraintsForSimulcast2;
            self.getSimulcastEncodings = getSimulcastEncodings;
            self.reapplySimulcastEncodings = reapplySimulcastEncodings;
            self.getSimulcastMinBitrate = getSimulcastMinBitrate;
            self.getSimulcastMaxBitrates = getSimulcastMaxBitrates;
            self.getSimulcastMaxFrameratesForH264 = getSimulcastMaxFrameratesForH264;

            self.pendingSimulcastEncodings = null; // stores any pending state to apply later. only for simulcast2.

            self.adjustOnHighBandwidth = true;
            self.adjustOnWindowSize = true;

            self.adjustOnVideoLow = true;
            self.adjustOnSlowLink = true;
            self.adjustMaxCutoff = true;

            // whether it is in low substream state due to high CPU or not.
            self.simulcastHighCpuState = false;
            self.updateSimulcastHighCpuState = updateSimulcastHighCpuState;

            // whether periodic speedtest downlink bandwidth is obtained or not?
            self.hasPeriodicBwDown = true;

            // config for various substream selection logic.

            self.substream = {
                base: {
                    // bitrate reduction estimate for temporal layer 0, 1, 2.
                    f0: 0.65,
                    f1: 0.8,
                    f2: 1,
                    // bitrate estimate for spatial layer 0, 1, 2
                    b0: 150,
                    b1: 500,
                    b2: 850
                },
                simulcast2: {
                    // for simulcast 2, assume rx is same as tx.
                    // assume default bitrate settings.
                    // bitrate limit for substream 0 (default), 1 (default), 2 (fixed)
                    // for 2, do not use high, as that is lower bound.
                    b0: 70,
                    b1: 300,
                    b2: 1000
                },
                h264: {
                    // temporal is unavailable in h264.
                    f0: 1,
                    f1: 1,
                    f2: 1
                },
                bw: {
                    // adjust based on bandwidth
                    enabled: true,

                    // do not use bandwidth if periodic value is not obtained (ANYAM-101374)
                    ifPeriodic: true,

                    // limit max bitrate, to avoid excessive CPU usage
                    limit: 5000,
                    // default is used when adjustOnHighBandwidth is false.
                    default: 2000
                },
                low: {
                    // if videoLow is enabled, force lowest substream.
                    // if count is 11 change to 20.
                    cutoff: 11,
                    limit: 20
                },
                disabled: {
                    // disabled video use lowest substream.
                    substream: 0,
                    temporal: 0
                },
                maxvideo: {
                    // maxvideo in spotlight layout
                    substream: 2,
                    temporal: 2
                },
                minvideo: {
                    // smaller videos in spotlight layout
                    // assuming 1mb/s for spotlight + audio, remaining video can be used by
                    // lower spatial and temporal layers as follows for [s,t]:
                    // [1,2], [1,1], [1,0], [0,1], [0,0]

                    // default cutoffs allow old Chrome publisher videos within 1mb/s for receiver,
                    // and new Chrome publisher videos within 0.6mb/s.
                    // cutoff count excludes local video.
                    max12: 2,
                    max11: 2,
                    max10: 3,
                    max01: 6,

                    // if adjustOnWindowSize is set, then area for 0 and 1.
                    usesize: false,
                    area0: Math.round(320 * 180 * 1.2), // 120%
                    area1: Math.round(640 * 360 * 1.2),

                    // if too many videos, then lower the temporal even if high bandwidth available.

                    // cutoff for temporal 0 and 1
                    temporal0: 8,
                    temporal1: 6,

                    // lower the substream and temporal if too many videos; more so on small size window.

                    cutoff: true,

                    // further cutoff for large display size
                    n12: 3,
                    n11: 4,
                    n10: 5,
                    n00: 7,

                    // cutoff display width
                    width: 1920,

                    // further cutoff for small display size
                    n12s: 1,
                    n11s: 3,
                    n10s: 4,
                    n00s: 5
                },
                gridvideo: {
                    // videos in grid/group layout

                    // Similar to above, videos with lower spatial and temporal layers as follows.
                    // [2,2], [2,1], [1,2], [1,1], [1,0], [0,1], [0,0]

                    // default cutoffs allow old Chrome publisher videos within 2 mb/s for receiver,
                    // and new Chrome within 1.6mb/s for receiver.

                    // old Chrome cutoffs: 2,3,4,5,6,16
                    // new Chrome cutoffs: 2,2,6,6,10,20
                    // use smaller of the two cases for each cutoff:

                    m22: 2,
                    m21: 2,
                    m12: 4,
                    m11: 5,
                    m10: 6,
                    m01: 16,

                    // if adjustOnWindowSize is set, then area for 0 and 1.
                    usesize: false,
                    area0: Math.round(320 * 180 * 1.2), // 120%
                    area1: Math.round(640 * 360 * 1.2),

                    // lower the substream and temporal if too many videos; more so on small size window.
                    cutoff: true,

                    s12: 6,
                    s11: 9,
                    s10: 9,
                    s00: 16,

                    // cutoff display width
                    width: 1920,

                    // further cutoff for small display size
                    s12s: 4,
                    s11s: 6,
                    s10s: 8,
                    s00s: 12
                },
                highcpu: {
                    // use lower substream if high cpu is detected for some duration.
                    enabled: true,

                    // how much to reduce in substream if high cpu detected.
                    delta: 1,

                    // substream reduction cutoff for cpu%.
                    cutoff: 50,

                    // duration in ms for substream CPU cutoff to trigger.
                    tcutoff: 20000,

                    // substream recover cutoff for cpu%
                    recover: 40,

                    // duration in ms for recovery to trigger. must be lower than maxSamples.
                    trecover: 120000
                },
                slowlink: {
                    // use a lower substream if there are slowlink events in the last interval.

                    enabled: true,

                    maxCount: 10,

                    // how much to reduce substream depends on how many slowlink events.
                    // cutoff: and delta for < vs. >=
                    cutoff0: 3,
                    substream0: 1,
                    substream1: 2,

                    // similarly for temporal
                    // cutoff: and delta for < vs. >=
                    cutoff1: 4,
                    temporal0: 1,
                    temporal1: 2
                }
            };

            self.parseAdjustSubstream = parseAdjustSubstream;

            self.switchSimulcastOnLowBitrateIfNeeded = switchSimulcastOnLowBitrateIfNeeded;
        }

        // initialize from config
        function initSimulcast() {
            // whether simulcast is enabled or not and at what participants count?
            var simulcastEnabled = configService.getValue('simulcastEnabled');
            if (simulcastEnabled) {
                self.simulcastAt = simulcastEnabled != 'false' ? 0 : self.simulcastAtMax; // TODO: allow integer in URL param.
            }

            // TODO: due to incompatibilities and differences in various versions of chrome we use, disable useSimulcast2 by default.
            // it was disabled already for: Unite 73, Mobile no, Chrome, if videoBitrate=auto, chrome on window, h264 video;
            // it was enabled for only narrow set of chrome/camaro on OSX. We do not know the impact of this change yet, but will
            // likely be good for compatibility when different devices are used in a call.

            self.useSimulcast2 = false;

            // hack for ANYAM-15071 - windows simulcast switching causes issues if using simulcast2 in janus.
            if (
                self.videocodec == 'h264' ||
                (helperService.isWindows() && $window.Janus.webRTCAdapter.browserDetails.browser === 'chrome')
            ) {
                logService.log('videoroomCtrl setting useSimulcast2=false on Windows Chrome or with H264');
                self.useSimulcast2 = false;
            }

            self.adjustOnHighBandwidth = (configService.getValue('adjustOnHighBandwidth') || 'true') == 'true';
            self.adjustOnWindowSize = (configService.getValue('adjustOnWindowSize') || 'true') == 'true';
            self.adjustOnVideoLow = (configService.getValue('adjustOnVideoLow') || 'true') == 'true';
            self.adjustOnSlowLink = (configService.getValue('adjustOnSlowLink') || 'true') == 'true';
            self.adjustMaxCutoff = (configService.getValue('adjustMaxCutoff') || 'true') == 'true';

            self.parseAdjustSubstream(configService.getValue('adjustSubstream'));
        }

        // initialize from config
        function initSimulcast2() {
            if (self.bitrate.adjust) {
                // if videoBitrate specified to auto-adjust, then disable useSimulcast2
                logService.log('videoroomCtrl setting useSimulcast2=false for auto-adjust videoBitrate');
                self.useSimulcast2 = false;
            }

            self.fixVideoFilterForSimulcast2 = configService.getClientValue('fixVideoFilterForSimulcast2') !== 'false';

            // use useSimulcast2 (transceiver based) param to override previous value.
            var useSimulcast2 = configService.getValue('useSimulcast2');
            if (!useSimulcast2) {
                self.useSimulcast2 = 'medium:470000,low:130000,fpsAuto:12';
            }
            if (useSimulcast2 && useSimulcast2 != 'none') {
                self.useSimulcast2 = useSimulcast2 == 'true' ? true : useSimulcast2 == 'false' ? false : useSimulcast2;
            } // else use what was set previously, instead of setting to null here.

            // VMware does not support simulcast2. Hence we will keep self.useSimulcast2 as false in case of VMware
            if (self.useSimulcast2 && self.VDIRef?.getVdiType() === executionEnvironmentEnum.VMware) {
                logService.log('videoroomCtrl resetting useSimulcast2 due to VDI to false from ' + useSimulcast2);
                self.useSimulcast2 = false;
            }

            if (self.useSimulcast2) {
                // TODO: does not revert if disabled during the call.
                self.updateConstraintsForSimulcast2();
                if (!self.fixVideoFilterForSimulcast2) {
                    self.applyConstraintsForSimulcast2();
                }
            }

            self.setConfigObjectFromValue(
                'bitrateLow',
                configService.getValue('bitrateLow'),
                self.bitrateLowConfig,
                self.bitrateLowDefault
            );
        }

        // parse config to populate substream config.
        // example: value="slowlink.enabled:false,bw.limit:3000", will update substream.slowlink.enable=false and substrteam.bw.limit=3000
        function parseAdjustSubstream(value) {
            if (value && value != 'false' && typeof value === 'string') {
                value
                    .split(',')
                    .map(function (nv) {
                        var index = nv.indexOf(':');
                        var name = index >= 0 ? nv.substring(0, index) : nv;
                        var val = index >= 0 ? nv.substring(index + 1) : null;
                        index = name.indexOf('.');
                        var obj = index >= 0 ? name.substring(0, index) : name;
                        var attr = index >= 0 ? name.substring(index + 1) : null;
                        return { obj: obj, attr: attr, val: val };
                    })
                    .forEach(function (data) {
                        var existing =
                            data.obj && data.attr && self.substream[data.obj] && self.substream[data.obj][data.attr];
                        if (existing !== undefined) {
                            var val = data.val;
                            if (typeof existing === 'boolean') {
                                val = val === 'true';
                            } else if (typeof existing === 'number') {
                                val = parseFloat(val);
                            }
                            logger.log('adjustSubstream', data.obj + '.' + data.attr + ' = ' + JSON.stringify(val));
                            self.substream[data.obj][data.attr] = val;
                        } else {
                            logger.warn('parseAdjustSubstream', 'config not found: ' + data.obj + ' ' + data.attr);
                        }
                    });
            }
        }

        // call setSimulcast for all displayed remote video
        function setSimulcastForAllVideos() {
            $window.$('#videoroom-videos > div.remote:not(.audioonly)').each(function (index, div) {
                self.setSimulcast(div);
            });
        }

        // Update the simulcast receive property for a remote feed video element.
        // the algorithm uses number of enabled video elements and the max video if
        // available to determine which video element should use which substream or
        // temporal stream. If substream and temporal values are supplied, they get
        // used explicitly.
        // The self.speedtestBwDown is used to determine the substream in group layout.
        // If force is set, then it will send the request even if for the same value.
        // This is used only for testing fron JS console, to force a change.
        function setSimulcast(div, substreamValue, temporalValue, force) {
            if (div && div.remoteFeed && (!self.layoutPage || !div.remoteFeed.isScreen)) {
                // not for screen or file.
                if (self.layoutPage && self.layoutPageReuse.enabled && !div.remoteFeed._plugin) {
                    self.logLayout('setSimulcast on ' + div.remoteFeed._fid + ' ignored as not attached to plugin');
                    return;
                }

                if (self.layoutPage && !div.remoteFeed.rfid) {
                    div.pendingSimulcast = [substreamValue, temporalValue, force];
                    logService.log('videoroomCtrl setSimulcast put to pending as not attached to plugin yet');
                    return;
                }

                if (div.getAttribute('playing') == 'false') {
                    div.pendingSimulcast = [substreamValue, temporalValue, force];
                    logService.log('videoroomCtrl setSimulcast put to pending because the stream is not playing');
                    return;
                }

                var count = $window.$('#videoroom-videos > div[enable="true"]:not(.audioonly)').length;
                if (self.layoutPage && self.layoutPageData.layout == 'allhands') {
                    count = self.layoutPageData.realSize; // use the page size instead of actual number of videos, since there are empty spaces.
                }
                var disabled = div.getAttribute('enable') != 'true';
                var slowlink = div.slowLinkCount || 0;
                if (self.reduceSubstreamChange.enabled && div.slowLinkPeak && slowlink < div.slowLinkPeak) {
                    slowlink = div.slowLinkPeak;
                }
                var ismaxvideo = div == self.maxvideo;
                var message = { request: 'configure', substream: 2, temporal: 2 };

                var area = 0;
                if (self.adjustOnWindowSize || self.substream.minvideo.usesize || self.substream.gridvideo.usesize) {
                    // approximate area of each video div. actual area is not stable yet.
                    var container = $window.$('#videoroom-videos')[0] || {};
                    if (self.maxvideo) {
                        if (count > 1) {
                            area = (container.offsetWidth * container.offsetHeight * 0.3) / (count - 1); // assume 30% for smaller videos
                        }
                    } else {
                        if (count > 0) {
                            area = (container.offsetWidth * container.offsetHeight) / count;
                        }
                    }
                }

                // if local video is displayed, do not count it in substream bitrate calculation of remote video.
                if ($window.$('#videoroom-videos > div.local[enable="true"]:not(.audioonly)').length > 0) {
                    count -= 1;
                }

                self.selectSimulcastSubstream(message, disabled, ismaxvideo, count, slowlink, area);

                if (div.remoteFeed._bitrateLow && message.substream == 2) {
                    message.substream = 1;
                }

                if (substreamValue !== undefined) {
                    message.substream = substreamValue;
                }
                if (temporalValue !== undefined) {
                    message.temporal = temporalValue;
                }

                if (div.temporalLayers == 2 && message.temporal == 2) {
                    // publisher has only 2 temporal layers.
                    message.temporal = 1;
                }

                // simulcast was disabled, but simulcastStarted is true, so set substream=0 which will cause no change in most cases
                if (div.remoteFeed.simulcastStarted && self.simulcastToggleState.restricted) {
                    message.substream = 0;
                }

                var existing = div.getAttribute('simulcast');
                var newvalue = '' + message.substream + ',' + message.temporal;

                // send message if no change in simulcast.
                if (force || existing != newvalue) {
                    message.spatial_layer = message.substream;
                    message.temporal_layer = message.temporal;
                    logService.log(
                        `videoroomCtrl simulcast subscriber[${div.getAttribute('user')}] substream=${
                            message.substream
                        } temporal=${message.temporal} reason=${self.simulcastReason}`
                    );
                    div.remoteFeed.send({ message: message });
                    div.setAttribute('simulcast', newvalue);
                }

                if (div.remoteFeed.simulcastStarted && !self.simulcastToggleState.restricted) {
                    self.verifySimulcastLater1(div, message, existing != newvalue);
                }
            }
        }

        // Verify that the previous set simulcast request took effect, by checking the received event after a
        // brief timeout. If the received substream value is not same as that of the set value, then it attempts to
        // re-request the simulcast substream.
        // Also update the temporal layers count if 2 was requested but no value was received within timeout.
        // Do not perform the checks if simulcast is not yet started for the stream.
        function verifySimulcastLater1(div, message, was_sent) {
            // cancel previous timer
            if (div.timerSimulcast1) {
                self.layoutPageTimeoutCancelWithState(div.timerSimulcast1);
                delete div.timerSimulcast1;
            }

            // then if configure message was sent then schedule a timer to check the value
            if (was_sent) {
                // also delete the other timer, since new values are getting set.
                if (div.timerSimulcast2) {
                    self.layoutPageTimeoutCancelWithState(div.timerSimulcast2);
                    delete div.timerSimulcast2;
                }

                // if values are not correct after timeout, then attempt to do again.
                div.timerSimulcast1 = self.layoutPageTimeoutWithState(
                    { div: div },
                    function (state) {
                        var div = state.div;
                        delete div.timerSimulcast1;

                        if (div.remoteFeed && div.remoteFeed.simulcastStarted) {
                            // values from received event, may be NaN
                            var substream = parseInt(div.getAttribute('substream'));
                            var temporal = parseInt(div.getAttribute('temporal'));

                            // this may alter message.temporal
                            self.limitSimulcastTemporal(div, message, temporal);

                            if (isNaN(substream) || message.substream != substream) {
                                // re-request the change.
                                logService.log(
                                    'videoroomCtrl simulcast again after timeout substream=' +
                                        message.substream +
                                        ' temporal=' +
                                        message.temporal
                                );
                                div.remoteFeed.send({ message: message });
                                // TODO: should we retry again, if the second request also fails for some reason?
                            }
                        } else {
                            logService.log(
                                'videoroomCtrl simulcast again after timeout ignored, remote feed closed or simulcast not started'
                            );
                        }
                    },
                    self.verifySimulcastTimeout
                );
            }
        }

        // Verify that the simulcast event represents the requested value, and if not, then re-request after a timeout.
        // Also update the temporal layers count if 2 was requested but no value was received in timeout.
        // The actual parameter represents the actual substream value, and must be an integer 0,1,2.
        function verifySimulcastLater2(div, actual) {
            // if requested substream does not match the selected one from event, re-request after a timeout.

            if (div.timerSimulcast2) {
                self.layoutPageTimeoutCancelWithState(div.timerSimulcast2);
                delete div.timerSimulcast2;
            }

            // after timeout, if received substream is not same as requested one, then attempt to request again, once.
            div.timerSimulcast2 = self.layoutPageTimeoutWithState(
                { div: div },
                function (state) {
                    var div = state.div;
                    delete div.timerSimulcast2;
                    if (div.remoteFeed && div.remoteFeed.simulcastStarted) {
                        var match = (div.getAttribute('simulcast') || '').match(/^(\d+),(\d+)$/);
                        if (match) {
                            // requested simulcast values
                            var message = { request: 'configure', substream: 2, temporal: 2 };
                            message.spatial_layer = message.substream = parseInt(match[1]);
                            message.temporal_layer = message.temporal = parseInt(match[2]);

                            // actual temporal value is in temporal attribute, may be NaN
                            self.limitSimulcastTemporal(div, message, parseInt(div.getAttribute('temporal')));

                            if (message.substream != actual) {
                                logService.log(
                                    'videoroomCtrl simulcast requested is not same as actual substream: ' +
                                        message.substream +
                                        '!=' +
                                        actual
                                );
                                logService.log(
                                    'videoroomCtrl simulcast again after event timeout substream=' +
                                        message.substream +
                                        ' temporal=' +
                                        message.temporal
                                );
                                div.remoteFeed.send({ message: message });
                            } else {
                                logService.log(
                                    'videoroomCtrl simulcast requested is same as actual substream: ' + actual
                                );
                            }
                        } // else this should not happen
                    } else {
                        logService.log('videoroomCtrl simulcast again after event timeout ignored, remote feed closed');
                    }
                },
                self.verifySimulcastTimeout
            );
        }

        // If needed, set the temporal layers count for this feed, if requested a temporal of 2, but did not get that.
        // It does not send any configure request, but only adjusts the local state about current temporal value,
        // and max temporal layers count.
        function limitSimulcastTemporal(div, message, temporal) {
            if (div && !div.temporalLayers && (isNaN(temporal) || message.temporal != temporal)) {
                if (message.temporal == 2) {
                    // probably does not support 3-temporal layers.
                    // TODO: we need a better way to determine number of temporal layers.
                    // like RTCRtpSender.getCapabilities().codecs, once that is implemented in Chrome.
                    logService.log('videoroomCtrl simulcast temporal layers is restricted to 2');
                    div.temporalLayers = 2; // so that next time it is not attempted to set to 2.
                    message.temporal = message.temporal_layer = 1;
                    var newvalue = '' + message.substream + ',' + message.temporal;
                    div.setAttribute('simulcast', newvalue);
                    div.setAttribute('temporal', message.temporal);
                }
            }
        }

        // Cleanup the simulcast workaround timers created previously in the above functions.
        // This is called from cleanup of the plugin attachment in startVideoPlay function.
        function cleanupSimulcastTimer(div) {
            if (div) {
                if (div.timerSimulcast1) {
                    self.layoutPageTimeoutCancelWithState(div.timerSimulcast1);
                    delete div.timerSimulcast1;
                }
                if (div.timerSimulcast2) {
                    self.layoutPageTimeoutCancelWithState(div.timerSimulcast2);
                    delete div.timerSimulcast2;
                }
            }
        }

        // use the available information (number of videos, recent slow link events, last speed test
        // result, and on electron last CPU usage result) to determine the simulcast substream and temporal
        // layers to use. The message argument is used for input and output both.
        // This is receiver side only function, which does have information about publisher bitrates
        // for various simulcast substreams. It assume 850-500-150. In practice on new Chrome versions
        // which does simulcast2, it could be restricted to 900-300-100, but older Chrome arbitrarily
        // determines it as roughly 850-500-150 as ideal values.
        // Similarly, new Chrome uses two temporal layers whereas old chrome use three,
        // i.e., 30-30-15 vs 30-15-7 fps at three substream levels. The bitrate reduction is 0.65
        // in new Chrome going from 30 to 15, but is roughly 0.8 and 0.65 going from 30 to 15 and
        // 30 to 7 respectively in old Chrome. In this case, the factor of 0.8 and 0.65 is used below.
        function selectSimulcastSubstream(message, disabled, ismaxvideo, count, slowlink, area) {
            var conf = self.substream;
            var f0 = conf.base.f0,
                f1 = conf.base.f1,
                f2 = conf.base.f2;
            var b0 = conf.base.b0,
                b1 = conf.base.b1,
                b2 = conf.base.b2;
            let useBw = self.speedtest && self.speedtestBwDown && (!conf.bw.ifPeriodic || self.hasPeriodicBwDown);
            let bw = useBw ? Math.min(self.speedtestBwDown, conf.bw.limit) : 0;
            if (self.useSimulcast2 && self.useSimulcast2 != 'legacy') {
                // assume rx is same as tx. assume default bitrate setting.
                b0 = self.configForSimulcast2.low / 1000 || conf.simulcast2.b0;
                b1 = self.configForSimulcast2.medium / 1000 || conf.simulcast2.b1;
                b2 = conf.simulcast2.b2;
            }
            if (self.videocodec == 'h264') {
                // assume rx is same as tx. temporal is unavailable in h264. Should be done regardless of simulcast2.
                f0 = conf.h264.f0;
                f1 = conf.h264.f1;
                f2 = conf.h264.f2;
            }

            if (
                self.adjustOnVideoLow &&
                count >= conf.low.cutoff &&
                count < conf.low.limit &&
                self.getIsVideoLowLayout()
            ) {
                //force lowest substream if videoLowFromServer
                count = conf.low.limit;
            }

            if (disabled) {
                // TODO: the video should be stopped at janus itself via signaling.
                message.substream = conf.disabled.substream;
                message.temporal = conf.disabled.temporal;
            } else if (self.maxvideo) {
                if (ismaxvideo) {
                    message.substream = conf.maxvideo.substream;
                    message.temporal = conf.maxvideo.temporal;
                } else {
                    var max12 = conf.minvideo.max12,
                        max11 = conf.minvideo.max11,
                        max10 = conf.minvideo.max10,
                        max01 = conf.minvideo.max01;

                    if (conf.bw.enabled && bw && (self.adjustOnHighBandwidth || bw <= conf.bw.default)) {
                        max12 = Math.floor((bw - 1000) / (b1 * f2));
                        max11 = Math.floor((bw - 1000) / (b1 * f1));
                        max10 = Math.floor((bw - 1000) / (b1 * f0));
                        max01 = Math.floor((bw - 1000) / (b0 * f1));
                    }
                    // assumes sorted cutoffs
                    if (count <= max12) {
                        message.substream = 1;
                        message.temporal = 2;
                    } else if (count <= max11) {
                        message.substream = message.temporal = 1;
                    } else if (count <= max10) {
                        message.substream = 1;
                        message.temporal = 0;
                    } else if (count <= max01) {
                        message.substream = 0;
                        message.temporal = 1;
                    } else {
                        message.substream = message.temporal = 0;
                    }

                    if ((conf.minvideo.usesize || self.adjustOnWindowSize) && area) {
                        if (message.substream > 0 && area <= conf.minvideo.area0) {
                            message.substream = 0;
                        } else if (message.substream > 1 && area <= conf.minvideo.area1) {
                            message.substream = 1;
                        }
                    }

                    if (self.adjustMaxCutoff && conf.minvideo.cutoff) {
                        if (count > conf.minvideo.temporal0 && message.temporal > 0) {
                            message.temporal = 0;
                        } else if (count > conf.minvideo.temporal1 && message.temporal > 1) {
                            message.temporal = 1;
                        }

                        var n12 = conf.minvideo.n12,
                            n11 = conf.minvideo.n11,
                            n10 = conf.minvideo.n10,
                            n00 = conf.minvideo.n00;
                        if ($window.innerWidth < conf.minvideo.width) {
                            n12 = conf.minvideo.n12s;
                            n11 = conf.minvideo.n11s;
                            n10 = conf.minvideo.n10s;
                            n00 = conf.minvideo.n00s;
                        }

                        if (count > n00 && message.substream > 0) {
                            message.substream = 0;
                        } else if (count > n12 && message.substream > 1) {
                            message.substream = 1;
                        }
                        if (count > n10 && message.temporal > 0) {
                            message.temporal = 0;
                        } else if (count > n11 && message.temporal > 1) {
                            message.temporal = 1;
                        }
                    }
                }
            } else {
                var m22 = conf.gridvideo.m22,
                    m21 = conf.gridvideo.m21,
                    m12 = conf.gridvideo.m12,
                    m11 = conf.gridvideo.m11,
                    m10 = conf.gridvideo.m10,
                    m01 = conf.gridvideo.m01;

                if (conf.bw.enabled && bw && (self.adjustOnHighBandwidth || bw <= conf.bw.default)) {
                    m22 = Math.floor(bw / (b2 * f2));
                    m21 = Math.floor(bw / (b2 * f1));
                    m12 = Math.floor(bw / (b1 * f2));
                    m11 = Math.floor(bw / (b1 * f1));
                    m10 = Math.floor(bw / (b1 * f0));
                    m01 = Math.floor(bw / (b0 * f1));
                }

                // assumes sorted cutoffs
                if (count <= m22) {
                    message.substream = message.temporal = 2;
                } else if (count <= m21) {
                    message.substream = 2;
                    message.temporal = 1;
                } else if (count <= m12) {
                    message.substream = 1;
                    message.temporal = 2;
                } else if (count <= m11) {
                    message.substream = message.temporal = 1;
                } else if (count <= m10) {
                    message.substream = 1;
                    message.temporal = 0;
                } else if (count <= m01) {
                    message.substream = 0;
                    message.temporal = 1;
                } else {
                    message.substream = message.temporal = 0;
                }

                if ((conf.gridvideo.usesize || self.adjustOnWindowSize) && area) {
                    if (message.substream > 0 && area <= conf.gridvideo.area0) {
                        message.substream = 0;
                    } else if (message.substream > 1 && area <= conf.gridvideo.area1) {
                        message.substream = 1;
                    }
                }

                if (self.adjustMaxCutoff && conf.gridvideo.cutoff) {
                    var s12 = conf.gridvideo.s12,
                        s11 = conf.gridvideo.s11,
                        s10 = conf.gridvideo.s10,
                        s00 = conf.gridvideo.s00;
                    if ($window.innerWidth < conf.gridvideo.width) {
                        s12 = conf.gridvideo.s12s;
                        s11 = conf.gridvideo.s11s;
                        s10 = conf.gridvideo.s10s;
                        s00 = conf.gridvideo.s00s;
                    }

                    if (count > s00 && message.substream > 0) {
                        message.substream = 0;
                    } else if (count > s12 && message.substream > 1) {
                        message.substream = 1;
                    }
                    if (count > s10 && message.temporal > 0) {
                        message.temporal = 0;
                    } else if (count > s11 && message.temporal > 1) {
                        message.temporal = 1;
                    }
                }
            }

            // use CPU usage on electron app.
            if (
                conf.highcpu.enabled &&
                (helperService.isElectron() || self.videoLowConfig) &&
                self.simulcastHighCpuState
            ) {
                message.substream = Math.max(message.substream - conf.highcpu.delta, 0);
            }

            if (self.adjustOnSlowLink && conf.slowlink.enabled && slowlink > 0) {
                var delta = slowlink < conf.slowlink.cutoff0 ? conf.slowlink.substream0 : conf.slowlink.substream1;
                message.substream = Math.max(message.substream - delta, 0);
                delta = slowlink < conf.slowlink.cutoff1 ? conf.slowlink.temporal0 : conf.slowlink.temporal1;
                message.temporal = Math.max(message.temporal - delta, 0);
            }
        }

        // for useSimulcast2, update constraints for videoFilter and videoCanvas, or other places,
        // because lower resolution does not work with simulcast 2.
        function updateConstraintsForSimulcast2() {
            if (self.useSimulcast2 && self.useSimulcast2 != 'legacy') {
                var value = '' + self.useSimulcast2;
                if (value.indexOf(':') >= 0) {
                    var data = {}; // store parsed name:value
                    value
                        .split(',')
                        .map(function (nv) {
                            var index = nv.indexOf(':');
                            return {
                                name: index >= 0 ? nv.substring(0, index) : nv,
                                value: index >= 0 ? nv.substring(index + 1) : null
                            };
                        })
                        .forEach(function (nv) {
                            // allow boolean or number
                            var value =
                                nv.value === 'true' || nv.value === 'false' ? nv.value === 'true' : parseInt(nv.value);
                            if (value && self.configForSimulcast2[nv.name] !== undefined) {
                                self.configForSimulcast2[nv.name] = data[nv.name] = value;
                            }
                        });
                    // default fpsAuto of 5 if true or 0 if false.
                    if (data.fpsAuto === true || data.fpsAuto === false) {
                        self.configForSimulcast2.fpsAuto = data.fpsAuto = data.fpsAuto === true ? 5 : 0;
                    }
                    // if fpsAuto is set, use fpsMedium as 15 and fpsLow as 7.5 (8).
                    if (data.fpsAuto !== undefined && data.fpsMedium === undefined && data.fpsLow === undefined) {
                        self.configForSimulcast2.fpsMedium = 15;
                        self.configForSimulcast2.fpsLow = 8;
                    }
                }
            }
        }

        function applyConstraintsForSimulcast2() {
            if (self.useSimulcast2 && self.useSimulcast2 != 'legacy') {
                const constraints =
                    !self.fixVideoFilterForSimulcast2 || self.simulcastFlag
                        ? self.constraintsForSimulcast2
                        : self.constraintsWithoutSimulcast2;
                if (videoFilterService.videoFilter) {
                    // TODO: will interfere with ?videoFilter= change of width/height if any.
                    const previous = {
                        width: videoFilterService.videoFilter.width,
                        height: videoFilterService.videoFilter.height
                    };

                    Object.assign(videoFilterService.videoFilter, constraints);

                    if (previous.width !== constraints.width || previous.height !== constraints.height) {
                        if (self.fixVideoFilterForSimulcast2) {
                            // stop previous stream so that canvas is recreated with new sizes.
                            videoFilterService.stopVideoFilterStream();
                        }
                    }
                }
                if (self.videoCanvasData) {
                    Object.assign(self.videoCanvasData, constraints);
                }
                if (self.faceTrackData) {
                    Object.assign(self.faceTrackData, constraints);
                }
            }
        }

        // if actual webcam track settings is not correct for simulcast2, then disable simulcast2.
        function verifyConstraintsForSimulcast2(width, height) {
            return !(
                width &&
                height &&
                (width < self.constraintsForSimulcast2.width || height < self.constraintsForSimulcast2.height)
            );
        }

        // called only for useSimulcast2
        function getSimulcastEncodings() {
            var maxBitrates = self.getSimulcastMaxBitrates();

            // whether to enable high substream
            var highActive = true;
            if (self.useSimulcast2 && self.useSimulcast2 != 'legacy') {
                var hasCustomStream =
                    self.fixVideoFilterForSimulcast2 && self.videoroom && self.videoroom.hasCustomStream;
                highActive = !self.getIsVideoLow() && !hasCustomStream;
                if (!highActive) {
                    maxBitrates.high = 0;
                }
            }

            var result = [
                {
                    rid: 'h',
                    active: highActive,
                    maxBitrate: maxBitrates.high,
                    scalabilityMode: 'L1T3'
                },
                {
                    rid: 'm',
                    active: true,
                    maxBitrate: maxBitrates.medium,
                    scaleResolutionDownBy: self.configForSimulcast2.scaleMedium,
                    scalabilityMode: 'L1T3'
                },
                {
                    rid: 'l',
                    active: true,
                    maxBitrate: maxBitrates.low,
                    scaleResolutionDownBy: self.configForSimulcast2.scaleLow,
                    scalabilityMode: 'L1T3'
                }
            ];
            if (self.videocodec == 'h264') {
                // disable temporal scalability, but apply maxFramerate explicitly.
                var maxFps = self.getSimulcastMaxFrameratesForH264();
                result.forEach(function (item, index) {
                    item.scalabilityMode = 'L1T1';
                    if (index === 1 && maxFps.medium) {
                        item.maxFramerate = maxFps.medium;
                    } else if (index === 2 && maxFps.low) {
                        item.maxFramerate = maxFps.low;
                    }
                });
            }
            logger.log('getSimulcastEncodings', 'result=' + JSON.stringify(result));
            return result;
        }

        function getSimulcastMinBitrate() {
            return self.useSimulcast2
                ? self.configForSimulcast2.low +
                      self.configForSimulcast2.medium +
                      self.configForSimulcast2.medium * self.bitrateLowConfig.cutoffFactor
                : 0;
        }

        function getSimulcastMaxBitrates() {
            // substream bitrate values in generalSettingsCtrl and micWebcamOptionsModalCtrl are overridden here.
            // substream kb/s limit: 500+, 300-, 30-. Total may not match 1.5Mb/s for default.
            var customSubstream = self.useSimulcast2 && self.useSimulcast2 !== true && self.useSimulcast2 !== 'legacy';
            var maxBitrates = Object.assign({}, self.bitrate.substream);
            if (!maxBitrates.high || maxBitrates.high < self.configForSimulcast2.high) {
                maxBitrates.high = self.configForSimulcast2.high;
            }
            var usePrevious = true;
            if (self.videoLowConfig) {
                // automatically calculate medium and low based on videoLowConfig.
                var videoLow = self.getIsVideoLow();
                var factorMedium =
                    self.configForSimulcast2.medium / (self.configForSimulcast2.medium + self.configForSimulcast2.low);
                var factorLow = 1 - factorMedium;
                if (videoLow === 'lower') {
                    // level 2
                    maxBitrates.medium = 1000 * Math.round(self.videoLowConfig.level2Bitrate * factorMedium);
                    maxBitrates.low = 1000 * Math.round(self.videoLowConfig.level2Bitrate * factorLow);
                    usePrevious = false;
                } else if (videoLow) {
                    // default videoLow mode, or level 1
                    maxBitrates.medium = 1000 * Math.round(self.videoLowConfig.level1Bitrate * factorMedium);
                    maxBitrates.low = 1000 * Math.round(self.videoLowConfig.level1Bitrate * factorLow);
                    usePrevious = false;
                }
            }

            if (
                self.bitrateLowConfig.type === 'encodings' &&
                !self.getIsVideoLow() &&
                self.useSimulcast2 &&
                self.bitrate.adjust
            ) {
                // adjust all substream according to total bitrate
                const totalBitrate = self.bitrate.adjust.video;
                const highBitrate = totalBitrate - self.configForSimulcast2.medium - self.configForSimulcast2.low;
                if (highBitrate < self.configForSimulcast2.medium * self.bitrateLowConfig.cutoffScale) {
                    // not enough bitrate for high substream
                    const lowFactor = self.configForSimulcast2.low / self.configForSimulcast2.medium;
                    let denom = self.bitrateLowConfig.cutoffScale + 1 + lowFactor;
                    if (totalBitrate < self.configForSimulcast2.medium + self.configForSimulcast2.low) {
                        denom = 1 + lowFactor;
                        maxBitrates.high = 0;
                    } else {
                        maxBitrates.high = Math.round(totalBitrate / denom) * self.bitrateLowConfig.cutoffScale;
                    }
                    maxBitrates.medium = Math.round(totalBitrate / denom);
                    maxBitrates.low = Math.round((totalBitrate / denom) * lowFactor);
                } else {
                    // enough bitrate for high substream. do not change high.
                    maxBitrates.medium = self.configForSimulcast2.medium;
                    maxBitrates.low = self.configForSimulcast2.low;
                }
                usePrevious = false;
            }

            if (usePrevious) {
                if (!maxBitrates.medium || customSubstream || maxBitrates.medium > self.configForSimulcast2.medium) {
                    maxBitrates.medium = self.configForSimulcast2.medium;
                }
                if (!maxBitrates.low || customSubstream || maxBitrates.low > self.configForSimulcast2.low) {
                    maxBitrates.low = self.configForSimulcast2.low;
                }
            }
            return maxBitrates;
        }

        // only return limit for medium and low.
        function getSimulcastMaxFrameratesForH264(alwaysSet) {
            var frameRate = self.getMaxFrameRate();
            var result = {};
            if (frameRate > 15 || alwaysSet) {
                // limit substream 1 and 0 at max 15fps. Why not 0 at 7fps?
                result.medium = result.low = 15;
            }
            // if configured, apply fps for medium and low substream.
            var simulcast2 = self.configForSimulcast2;
            if (!simulcast2.fpsAuto || self.videoFeedCount >= simulcast2.fpsAuto) {
                if (simulcast2.fpsMedium > 0 && simulcast2.fpsMedium < 15) {
                    result.medium = simulcast2.fpsMedium;
                }
                if (simulcast2.fpsLow > 0 && simulcast2.fpsLow < 15) {
                    result.low = simulcast2.fpsLow;
                }
            }
            if (self.videoLowConfig) {
                var config = self.videoLowConfig;
                var videoLow = self.getIsVideoLow();
                if (videoLow === 'lower') {
                    if (!result.medium || result.medium > config.level2FpsMedium) {
                        result.medium = config.level2FpsMedium;
                    }
                    if (!result.low || result.low > config.level2FpsLow) {
                        result.low = config.level2FpsLow;
                    }
                } else if (videoLow) {
                    if (!result.medium || result.medium > config.level1FpsMedium) {
                        result.medium = config.level1FpsMedium;
                    }
                    if (!result.low || result.low > config.level1FpsLow) {
                        result.low = config.level1FpsLow;
                    }
                }
            }
            return result;
        }

        // for useSimulcast2, whether to disable or enable substream 0 depends on other attrs.
        // this is called when publish resolution, bitrate or framerate is altered, e.g., due to
        // transition to/from videoLow mode.
        function reapplySimulcastEncodings(fpsChange) {
            logger.log('reapplySimulcastEncodings', `fpsChange=${fpsChange}`);
            var hasCustomStream = self.fixVideoFilterForSimulcast2 && self.videoroom && self.videoroom.hasCustomStream;
            var newValue = !self.getIsVideoLow() && !hasCustomStream;

            var pendingReason = null;
            if (self.pendingSimulcastEncodings) {
                // if a pending exists, use its fpsChange if needed.
                logger.log(
                    'reapplySimulcastEncodings',
                    `has pending fpsChange=${self.pendingSimulcastEncodings.fpsChange}`
                );
                if (!fpsChange && self.pendingSimulcastEncodings.fpsChange) {
                    fpsChange = true;
                }
                // avoid reapplying from pending later.
                self.pendingSimulcastEncodings = null;
            }

            if (self.videoroom && self.videoroom.webrtcStuff && self.videoroom.webrtcStuff.pc) {
                var sender = self.videoroom.webrtcStuff.pc.getSenders().find(function (sender) {
                    return sender.track.kind === 'video';
                });
                if (sender) {
                    var parameters = sender.getParameters();
                    if (parameters && parameters.encodings && parameters.encodings.length > 0) {
                        var oldValue = parameters.encodings[0].active;
                        var isH264 = self.videocodec === 'h264';
                        var fpsAuto = isH264 && fpsChange && self.configForSimulcast2.fpsAuto;
                        var maxBitrates = self.getSimulcastMaxBitrates();
                        var bitrateChanged =
                            parameters.encodings.length >= 3
                                ? parameters.encodings[1].maxBitrate !== maxBitrates.medium ||
                                  parameters.encodings[2].maxBitrate !== maxBitrates.low
                                : false;
                        var maxFps = isH264 ? self.getSimulcastMaxFrameratesForH264() : null;
                        var fpsChanged =
                            isH264 && parameters.encodings.length >= 3
                                ? parameters.encodings[1].maxFramerate !== maxFps.medium ||
                                  parameters.encodings[2].maxFramerate !== maxFps.low
                                : false;

                        if (!maxBitrates.high && newValue) {
                            newValue = false; // disable high substream if disabled by maxBitrates. only if bitrateLowConfig.type is "encodings"
                        }

                        if (oldValue != newValue || fpsAuto || fpsChanged || bitrateChanged) {
                            logger.log(
                                'reapplySimulcastEncodings',
                                `changing substream, if ${oldValue}!=${newValue} or fpsAuto=${fpsAuto} or fpsChanged=${fpsChanged} or bitrateChanged=${bitrateChanged}, fpsChange=${fpsChange}`
                            );

                            parameters.encodings[0].active = newValue;

                            if (parameters.encodings[0].maxBitrate !== maxBitrates.high) {
                                parameters.encodings[0].maxBitrate = maxBitrates.high;
                            }

                            if ((isH264 && (fpsAuto || fpsChanged)) || bitrateChanged) {
                                // if fpsAuto is set, and change is desired, apply change in fps too.
                                // since changing maxFramerate does not work directly, first mark them inactive, and then active again with maxFramerate
                                parameters.encodings[1].active = parameters.encodings[2].active = false;

                                var maxFramerates = self.getSimulcastMaxFrameratesForH264(true); // called again with defaults
                                logger.log(
                                    'reapplySimulcastEncodings',
                                    `changing [m,l] fps to ${maxFramerates.medium},${maxFramerates.low}, bitrate to ${maxBitrates.medium},${maxBitrates.low}`
                                );

                                sender
                                    .setParameters(parameters)
                                    .then(function () {
                                        var parameters = sender.getParameters();
                                        parameters.encodings[1].active = parameters.encodings[2].active = true;
                                        parameters.encodings[1].maxFramerate = maxFramerates.medium;
                                        parameters.encodings[2].maxFramerate = maxFramerates.low;
                                        if (bitrateChanged) {
                                            if (parameters.encodings[1].maxBitrate !== maxBitrates.medium) {
                                                parameters.encodings[1].maxBitrate = maxBitrates.medium;
                                            }
                                            if (parameters.encodings[2].maxBitrate !== maxBitrates.low) {
                                                parameters.encodings[2].maxBitrate = maxBitrates.low;
                                            }
                                        }

                                        sender.setParameters(parameters).catch(function (error) {
                                            logger.error(
                                                'reapplySimulcastEncodings',
                                                'failed to apply sender parameters second time',
                                                error
                                            );
                                        });
                                    })
                                    .catch(function (error) {
                                        logger.error(
                                            'reapplySimulcastEncodings',
                                            'failed to apply sender parameters first time',
                                            error
                                        );
                                    });
                            } else {
                                sender.setParameters(parameters).catch(function (error) {
                                    logger.error(
                                        'reapplySimulcastEncodings',
                                        'failed to apply sender parameters',
                                        error
                                    );
                                });
                            }
                        } else {
                            logger.log(
                                'reapplySimulcastEncodings',
                                `not changing substream oldValue=${oldValue} newValue=${newValue} fpsAuto=${fpsAuto}`
                            );
                        }
                    } else {
                        logger.log('reapplySimulcastEncodings', 'no encodings found');
                    }
                } else {
                    pendingReason = 'no video sender';
                }
            } else {
                pendingReason = 'no peer connection';
            }

            if (pendingReason) {
                // will reapply from pending after peer connection is established and video is flowing.
                if (!self.pendingSimulcastEncodings) {
                    self.pendingSimulcastEncodings = {};
                }

                self.pendingSimulcastEncodings.fpsChange = !!(self.pendingSimulcastEncodings.fpsChange || fpsChange);
                logger.log(
                    'reapplySimulcastEncodings',
                    `put to pending due to ${pendingReason} fpsChange=${self.pendingSimulcastEncodings.fpsChange}`
                );
            }
        }

        // determine whether the current CPU condition under videoLowConfig calls for
        // reducing the substream selection. The data argument is same as self.cpuUsageData
        // but supplied for modularity from videoLowPerformanceService. If checkReduce is
        // false, then it forces state to be false, e.g., due to recent videoLow transition.
        function updateSimulcastHighCpuState(checkReduce, data) {
            if (!self.substream.highcpu.enabled) {
                return;
            }

            var oldValue = self.simulcastHighCpuState;
            var highCpuState = null; // or true, false
            if (checkReduce && data.length >= 2) {
                // check if substream should be reduced or recovered now.
                var highcpu = self.substream.highcpu;
                var interval = !oldValue ? highcpu.tcutoff : highcpu.trecover;
                var ts0 = data[0].ts - interval; // beginning of valid interval
                if (ts0 >= data[data.length - 1].ts) {
                    // have enough samples
                    var cutoff = !oldValue ? highcpu.cutoff : highcpu.recover;
                    var transition = true;
                    var count = 0;
                    for (var j = 0; j < data.length; ++j) {
                        var obj = data[j];
                        if (
                            obj.type !== 'cpu' ||
                            (!oldValue && obj.value < cutoff) ||
                            (oldValue && obj.value >= cutoff)
                        ) {
                            transition = false; // not enough data, or no consistency
                            break;
                        }
                        ++count;
                        if (obj.ts < ts0) {
                            break; // no need to process older items.
                        }
                    }
                    if (transition && count >= 2) {
                        // atleast two samples should be present to transition.
                        highCpuState = !oldValue;
                    }
                }
            }
            if (highCpuState !== null && oldValue != highCpuState) {
                logger.log('updateSimulcastHighCpuState', `highCpuState=${oldValue}=>${highCpuState}`);
                self.simulcastHighCpuState = highCpuState;
                // trigger substream selection on change.
                $timeout(function () {
                    self.setSimulcastForAllVideos();
                });
            }
        }

        // called from stats to switch from substream 2 to 1, if substream 2 bitrate is lower than 1, when useSimulcast2.
        function switchSimulcastOnLowBitrateIfNeeded(indexId, substream, bitrate) {
            if (
                self.bitrateLowConfig.type == 'substream' &&
                self.useSimulcast2 &&
                substream &&
                substream == '2' &&
                bitrate * 1000 < self.configForSimulcast2.medium * self.bitrateLowConfig.cutoffFactor
            ) {
                const fid = indexId.split('-')[0];
                const div = $window.$('#videoroom-videos > div.remote[user="' + fid + '"]:not(.audioonly)')[0];
                if (div && div.remoteFeed && !div.remoteFeed._bitrateLowTimer) {
                    logger.log(
                        'switchSimulcastOnLowBitrateIfNeeded',
                        `${fid} substream 2 bitrate is too low ${bitrate * 1000} < ${
                            self.configForSimulcast2.medium
                        } * ${self.bitrateLowConfig.cutoffFactor}`
                    );

                    div.remoteFeed._bitrateLow = true;
                    self.setSimulcast(div);

                    div.remoteFeed._bitrateLowTimer = $timeout(function () {
                        delete div.remoteFeed._bitrateLowTimer;
                        if (div.remoteFeed._bitrateLow) {
                            delete div.remoteFeed._bitrateLow;
                            logger.log('switchSimulcastOnLowBitrateIfNeeded', 'reverting bitrateLow after timeout');
                            self.setSimulcast(div);
                        }
                        div.remoteFeed._bitrateLowTimer = $timeout(function () {
                            delete div.remoteFeed._bitrateLowTimer;
                        }, self.bitrateLowConfig.noChangeAfter);
                    }, self.bitrateLowConfig.resetAfter);
                }
            }
        }
    }
]);
